// Unified search pipeline.
//
// Entry point: a free-text query. The query is classified into one or more
// categories (speciality name, UK symptom, CCSD code, specialist name), each
// category is resolved back to specialities, and the speciality scope is
// expanded with the "Relevant Specialties" from the speciality page mapping
// (the uploaded workbook). Specialists and blogs are then fetched *through*
// that scope only — via speciality names and the derived Compucare
// categoryId relationship (specialityCategoryLink.ts).

import type { DoctorCard } from '@/types/doctor';

import {
  getCategoryIdsForSpecialityNames,
  getCategoryIdForDoctorSlug,
  specialityNamesMatch,
} from '../data/specialityCategoryLink';
import { searchBlogs, type BlogSearchResult } from '../data/searchBlogs';
import { searchCcsdEntries, type CcsdEntry } from '../data/ccsdCodes';
import { isCcsdSearchEnvironmentAllowed } from '../data/ccsdSearchGate';
import { searchSymptoms, type SymptomEntry } from '../data/symptoms';
import {
  resolveCcsdCodeToDoctorsDirect,
  buildDoctorCardsFromDirectCcsdMatch,
} from '../data/ccsdCodeToDoctorMap';
import { SPEC_OPTION_TITLE_BY_SLUG } from '../constants/specialityOptions';
import {
  SPECIALITY_PAGE_MAPPING,
  type SpecialityPageMappingEntry,
} from '../data/specialityPageMapping';
import { searchDoctors } from './searchService';

export interface SpecialityScopeEntry {
  pageName: string;
  path: string;
  pageType: string;
  primarySpeciality: string;
  relatedSpecialities: string[];
  /** True when the row matched the query directly (not via expansion). */
  matched: boolean;
}

export interface UnifiedSearchResult {
  /** Matched speciality pages (+ related speciality pages) from the mapping. */
  specialities: SpecialityScopeEntry[];
  /** CCSD entries matching the query (code or treatment text). */
  ccsd: CcsdEntry[];
  /** Specialists: name matches OR specialities within the resolved scope. */
  doctors: DoctorCard[];
  /** Blogs whose speciality falls within the resolved scope. */
  blogs: BlogSearchResult[];
}

const MIN_QUERY_LENGTH = 2;

const rowNames = (row: SpecialityPageMappingEntry): string[] => [
  row.pageName,
  row.primarySpeciality,
];

const toScopeEntry = (
  row: SpecialityPageMappingEntry,
  matched: boolean,
): SpecialityScopeEntry => ({
  pageName: row.pageName,
  path: row.path,
  pageType: row.pageType,
  primarySpeciality: row.primarySpeciality,
  relatedSpecialities: row.relevantSpecialities,
  matched,
});

/** Rows that ARE one of the given speciality names (pageName/primary match). */
const rowsForSpecialityNames = (
  names: Iterable<string>,
): SpecialityScopeEntry[] => {
  const rows: SpecialityScopeEntry[] = [];
  const seen = new Set<string>();
  for (const row of SPECIALITY_PAGE_MAPPING) {
    if (seen.has(row.path)) continue;
    const isMatch = rowNames(row).some((rowName) =>
      [...names].some((name) => specialityNamesMatch(rowName, name)),
    );
    if (isMatch) {
      seen.add(row.path);
      rows.push(toScopeEntry(row, true));
    }
  }
  return rows;
};

const rowMatchesQuery = (
  row: SpecialityPageMappingEntry,
  queryTokens: string[],
): boolean => {
  const names = [...rowNames(row), ...row.relevantSpecialities].map((n) =>
    n.toLowerCase(),
  );
  // Every query token must appear in at least one of the row's names.
  return queryTokens.every((token) =>
    names.some((name) => name.includes(token)),
  );
};

/**
 * Resolves the search scope from the speciality page mapping:
 * matched rows + their related specialities (rows that ARE one of the
 * matched row's "Relevant Specialties").
 */
const resolveScope = (
  query: string,
  ccsdMatches: CcsdEntry[],
  symptomMatches: SymptomEntry[],
): SpecialityScopeEntry[] => {
  const queryTokens = query
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 2);

  const matched = new Map<string, SpecialityScopeEntry>();

  // 1. Direct speciality-name match.
  if (queryTokens.length > 0) {
    for (const row of SPECIALITY_PAGE_MAPPING) {
      if (rowMatchesQuery(row, queryTokens)) {
        matched.set(row.path, toScopeEntry(row, true));
      }
    }
  }

  // 2. CCSD code → mapped speciality names → mapping rows.
  for (const name of ccsdMatches.flatMap((e) => e.specialities)) {
    for (const entry of rowsForSpecialityNames([name])) {
      matched.set(entry.path, entry);
    }
  }

  // 3. Symptom → speciality slugs → titles → mapping rows.
  const symptomTitles = symptomMatches.flatMap((s) =>
    s.specialities
      .map((slug) => SPEC_OPTION_TITLE_BY_SLUG[slug])
      .filter(Boolean),
  );
  for (const title of symptomTitles) {
    for (const entry of rowsForSpecialityNames([title])) {
      matched.set(entry.path, entry);
    }
  }

  // 4. Expansion — related specialities of every matched row
  //    (rows that ARE one of the matched row's relevant specialities).
  const expanded = new Map(matched);
  for (const entry of matched.values()) {
    for (const related of entry.relatedSpecialities) {
      for (const row of SPECIALITY_PAGE_MAPPING) {
        if (expanded.has(row.path)) continue;
        const isRelated = rowNames(row).some((n) =>
          specialityNamesMatch(n, related),
        );
        if (isRelated) {
          expanded.set(row.path, toScopeEntry(row, false));
        }
      }
    }
  }

  return [...expanded.values()];
};

/** Doctor matches the query as a person's name (all tokens in fullName). */
const isDoctorNameMatch = (
  doctor: DoctorCard,
  queryTokens: string[],
): boolean =>
  queryTokens.length > 0 &&
  queryTokens.every((token) =>
    (doctor.fullName ?? '').toLowerCase().includes(token),
  );

const isDoctorInScope = (
  doctor: DoctorCard,
  scopeNames: string[],
  scopeCategoryIds: string[],
): boolean => {
  const categoryId = getCategoryIdForDoctorSlug(doctor.slug);
  if (categoryId && scopeCategoryIds.includes(categoryId)) return true;

  const doctorNames = [
    doctor.primarySpeciality ?? '',
    ...(doctor.specialities ?? []),
  ];
  return doctorNames.some((doctorName) =>
    scopeNames.some((scopeName) => specialityNamesMatch(doctorName, scopeName)),
  );
};

const isBlogInScope = (blog: BlogSearchResult, scopeNames: string[]): boolean =>
  !!blog.speciality &&
  scopeNames.some((scopeName) =>
    specialityNamesMatch(blog.speciality as string, scopeName),
  );

/**
 * Classifies the query and resolves all three result columns through the
 * speciality scope derived from the speciality page mapping.
 */
export async function resolveUnifiedSearch(
  query: string,
  options?: { specialitySlugs?: string[] },
): Promise<UnifiedSearchResult> {
  const trimmed = query.trim();
  if (trimmed.length < MIN_QUERY_LENGTH) {
    return { specialities: [], ccsd: [], doctors: [], blogs: [] };
  }

  const selectedSlugs = options?.specialitySlugs ?? [];
  const selectedTitles = selectedSlugs
    .map((slug) => SPEC_OPTION_TITLE_BY_SLUG[slug])
    .filter(Boolean);

  const [ccsdMatches, symptomMatches, doctorResponse, blogMatches] =
    await Promise.all([
      // CCSD code matching is gated by the runtime feature flag (client-safe
      // helper reading `/api/feature-flags` with the env-scoped registry as
      // fallback), so a dashboard toggle applies on the suggestion path too.
      (async () =>
        (await isCcsdSearchEnvironmentAllowed())
          ? searchCcsdEntries(trimmed)
          : [])(),
      Promise.resolve(searchSymptoms(trimmed)),
      searchDoctors({
        query: trimmed,
        mainOnly: true,
        speciality:
          selectedSlugs.length > 0 ? selectedSlugs.join(',') : undefined,
      }),
      Promise.resolve(searchBlogs(trimmed)),
    ]);

  const scope = resolveScope(trimmed, ccsdMatches, symptomMatches);

  // Narrow the speciality scope when the dropdown filter is active.
  const filteredScope =
    selectedTitles.length > 0
      ? scope.filter((entry) =>
          selectedTitles.some(
            (title) =>
              specialityNamesMatch(title, entry.pageName) ||
              specialityNamesMatch(title, entry.primarySpeciality),
          ),
        )
      : scope;

  const scopeNames = [
    ...new Set(
      filteredScope.flatMap((entry) => [
        entry.pageName,
        entry.primarySpeciality,
        ...entry.relatedSpecialities,
      ]),
    ),
  ];
  const scopeCategoryIds = getCategoryIdsForSpecialityNames(scopeNames);

  const queryTokens = trimmed
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 2);

  const doctors = doctorResponse.items.filter(
    (doctor) =>
      isDoctorNameMatch(doctor, queryTokens) ||
      isDoctorInScope(doctor, scopeNames, scopeCategoryIds),
  );

  // Temporary fallback: when a CCSD code has a direct mapping in
  // ccsdCodeToDoctorMap.json, replace the doctors array with only those
  // direct matches so the code resolves to exactly its linked clinician(s) —
  // no other search results. This bypasses the speciality→page→categoryId
  // chain and will be replaced once the specialist↔speciality↔blog
  // relationship is properly derived.
  const directCcsdMatches = resolveCcsdCodeToDoctorsDirect(trimmed);
  const directCcsdDoctors: DoctorCard[] =
    directCcsdMatches.length > 0
      ? buildDoctorCardsFromDirectCcsdMatch(directCcsdMatches)
      : [];

  const blogs = blogMatches.filter((blog) => isBlogInScope(blog, scopeNames));

  return {
    specialities: filteredScope,
    ccsd: ccsdMatches,
    doctors: directCcsdDoctors.length > 0 ? directCcsdDoctors : doctors,
    blogs,
  };
}
